import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import { faPenToSquare } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import {
  faCheckCircle,
  faRotateLeft,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { VariableDefinition } from '@repo/videa-components/src/ui/variable-textarea/types';
import { VariableTextarea } from '@repo/videa-components/src/ui/variable-textarea/VariableTextarea';

import { NotePMSStatusAlert } from './NotePMSStatusAlert';

export interface NoteEditorProps {
  noteContent: string;
  noteId?: string;
  feedbackTraceId?: string;
  feedbackWidget?: ReactNode;
  variableDefinitions?: VariableDefinition[];
  isNoteReadOnly: boolean | '' | null | undefined;
  isNoteSavedToPMS: boolean | null | undefined;
  isNoteBeingSavedToPMS: boolean | '' | null | undefined;
  isSavingNoteUpdate?: boolean;
  hasBeenEdited?: boolean;
  compactMode?: boolean;
  onNoteChange: (value: string) => void;
  onNoteClick?: () => void;
  onResetNote?: () => void;
  onVariableValueChange?: (varName: string, newValue: string) => void;
  onCustomizeFieldValues?: () => void;
  /** Translated label for "Customize Field Values" link in variable dropdown. */
  customizeFieldValuesLabel?: string;
  t?: (key: string | any) => string;
}

export const NoteEditor = ({
  noteContent,
  noteId,
  feedbackTraceId,
  feedbackWidget,
  variableDefinitions,
  isNoteReadOnly,
  isNoteSavedToPMS,
  isNoteBeingSavedToPMS,
  isSavingNoteUpdate = false,
  hasBeenEdited = false,
  compactMode = false,
  onNoteChange,
  onNoteClick,
  onResetNote,
  onVariableValueChange,
  onCustomizeFieldValues,
  customizeFieldValuesLabel,
  t = (key: string) => key,
}: NoteEditorProps) => {
  const textareaRef = useRef<HTMLDivElement>(null);
  const hasAutoFocusedRef = useRef(false);

  useEffect(() => {
    // Auto-focus on first render
    if (textareaRef.current && !isNoteReadOnly && !hasAutoFocusedRef.current) {
      const timeoutId = setTimeout(() => {
        if (textareaRef.current && !hasAutoFocusedRef.current) {
          textareaRef.current.focus();
          hasAutoFocusedRef.current = true;
        }
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [isNoteReadOnly]);

  return (
    <div
      className="vai-bg-primary-foreground vai-border vai-border-gray-200 vai-rounded-md vai-flex vai-flex-col vai-flex-1 vai-min-h-0"
      data-testid="note-editor-container"
    >
      {/* Header */}
      <div className="vai-flex vai-items-center vai-justify-between vai-px-4 vai-py-2 vai-flex-shrink-0">
        <div className="vai-flex vai-items-center vai-gap-2">
          {isNoteReadOnly ? (
            <NotePMSStatusAlert
              isNoteReadOnly={true}
              isNoteSavedToPMS={isNoteSavedToPMS}
              isNoteBeingSavedToPMS={isNoteBeingSavedToPMS}
            />
          ) : (
            <>
              <div style={{ fontSize: compactMode ? '0.875rem' : '1rem' }}>
                <FontAwesomeIcon
                  icon={faPenToSquare}
                  className="vai-text-black"
                />
              </div>
              <p
                className={`vai-text-black ${
                  compactMode ? 'vai-text-xs' : 'vai-text-[14px]'
                }`}
              >
                <span className="vai-font-semibold">
                  {t('Edit your note in Videa — help our AI learn your style.')}
                </span>
              </p>
            </>
          )}
        </div>
        <div className="vai-flex vai-items-center vai-gap-4">
          {/* Reset to Default button - only show when note has been edited and is editable */}
          {hasBeenEdited && !isNoteReadOnly && onResetNote && (
            <button
              onClick={onResetNote}
              className="vai-flex vai-items-center vai-gap-1.5 vai-text-xs vai-text-gray-500 hover:vai-text-gray-700 vai-font-medium vai-transition-colors"
              type="button"
            >
              <FontAwesomeIcon icon={faRotateLeft} className="vai-text-xs" />
              {t('Reset to Default')}
            </button>
          )}
          {/* Save status indicator */}
          <div className="vai-flex vai-items-center vai-gap-2">
            {!isNoteBeingSavedToPMS && !isNoteSavedToPMS && (
              <>
                {isSavingNoteUpdate ? (
                  <>
                    <div style={{ fontSize: compactMode ? '0.75rem' : '1rem' }}>
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="vai-text-green-600 vai-font-weight-900 vai-line-height-normal"
                      />
                    </div>
                    <p className="vai-font-inter vai-text-xs vai-text-black vai-font-medium vai-leading-none">
                      {t('Saving...')}
                    </p>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: compactMode ? '0.75rem' : '1rem' }}>
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="vai-text-green-600 vai-font-weight-900 vai-line-height-normal"
                      />
                    </div>
                    <p className="vai-font-inter vai-text-xs vai-text-green-600 vai-font-medium vai-leading-none">
                      {t('Changes Saved')}
                    </p>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="vai-border-t vai-border-gray-200 vai-flex-shrink-0" />

      {/* Content */}
      <div
        className="vai-flex vai-flex-col vai-px-4 vai-pt-4 vai-pb-4 vai-flex-1 vai-min-h-0 vai-overflow-hidden"
        data-testid="note-content-container"
      >
        <VariableTextarea
          key={noteId}
          ref={textareaRef}
          id="clinical-note"
          value={noteContent}
          variableDefinitions={variableDefinitions ?? []}
          isDisabled={!!isNoteReadOnly}
          isReadOnly={!!isNoteReadOnly}
          onClick={onNoteClick}
          onChange={onNoteChange}
          onVariableValueChange={onVariableValueChange}
          onCustomizeFieldValues={onCustomizeFieldValues}
          customizeFieldValuesLabel={customizeFieldValuesLabel}
          className={`vai-flex-1 vai-overflow-y-auto vai-resize-none vai-border-gray-300 vai-min-h-0 ${
            compactMode ? 'vai-text-sm vai-p-3' : 'vai-p-4'
          } ${isNoteReadOnly ? 'vai-bg-gray-100 vai-cursor-not-allowed' : ''}`}
        />
      </div>

      {/* Feedback Widget Section - Only show if feedbackTraceId exists and feedbackWidget is provided */}
      {noteId && feedbackTraceId && feedbackWidget && (
        <>
          <div className="vai-border-t vai-border-gray-200 vai-flex-shrink-0" />
          <div className="vai-flex vai-justify-center vai-px-4 vai-py-3">
            {feedbackWidget}
          </div>
        </>
      )}
    </div>
  );
};
